/**
 * Exportar / Importar flujos de trabajo entre cuentas.
 *
 * Al importar se REGENERAN todos los ids de step y se re-mapean TODAS sus referencias
 * (nextStepId/elseStepId y las embebidas como string: `step-<id>`, `step_<id>`,
 * `previousResults.step-<id>`). Como cada id de step es un string único y largo, se logra
 * reemplazando todas sus ocurrencias en el JSON serializado (two-pass con placeholder para
 * evitar colisiones/substrings). Las referencias a recursos de la cuenta origen se limpian.
 */

const EXPORT_TYPE = 'crm-workflow-export';

/** Campos de config que apuntan a recursos de la cuenta (se limpian al importar). */
const ACCOUNT_REF_FIELDS = [
  'templateId',
  'whatsappTemplateId',
  'listId',
  'salesTeamId',
  'emailProviderId',
  'fromEmail',
];

/** Construye el envelope de exportación (sin ids ni datos de cuenta). */
export function buildExport(fullWorkflow) {
  const wf = fullWorkflow?.data || fullWorkflow || {};
  return {
    _type: EXPORT_TYPE,
    version: 1,
    exportedAt: new Date().toISOString(),
    workflow: {
      name: wf.name || 'Flujo sin nombre',
      description: wf.description || '',
      workflowTrigger: wf.workflowTrigger || wf.trigger || { type: 'contact_created', conditions: {} },
      steps: Array.isArray(wf.steps) ? wf.steps : [],
    },
  };
}

/** Descarga un objeto como archivo .json. */
export function downloadJson(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'workflow.json';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Lee un File y devuelve el objeto JSON validado. */
export function readJsonFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const obj = JSON.parse(String(reader.result || ''));
        const wf = obj?.workflow || obj;
        if (obj?._type && obj._type !== EXPORT_TYPE) {
          return reject(new Error('El archivo no es un export de flujo de trabajo válido.'));
        }
        if (!wf || !Array.isArray(wf.steps)) {
          return reject(new Error('El JSON no contiene un flujo con steps válido.'));
        }
        resolve(obj);
      } catch (e) {
        reject(new Error('No se pudo leer el JSON: ' + (e?.message || 'archivo inválido')));
      }
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.readAsText(file);
  });
}

function slugify(name) {
  return String(name || 'flujo')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'flujo';
}

/** Nombre de archivo sugerido para exportar. */
export function exportFilename(name) {
  return `workflow-${slugify(name)}.json`;
}

/** Limpia las referencias a recursos de la cuenta en un objeto config (in place). */
function clearAccountRefs(config) {
  if (!config || typeof config !== 'object') return config;
  for (const f of ACCOUNT_REF_FIELDS) {
    if (f in config) config[f] = '';
  }
  return config;
}

/**
 * Toma el JSON importado y devuelve un workflow listo para el builder:
 * ids de step regenerados, referencias re-mapeadas, refs de cuenta limpias, sin id de workflow.
 */
export function regenerateForImport(parsed) {
  const src = parsed?.workflow || parsed || {};
  const steps = Array.isArray(src.steps) ? src.steps : [];

  // 1) Mapa oldId -> newId (newId único garantizado, formato step-…)
  const base = Date.now();
  const rand = () => Math.random().toString(36).slice(2, 8);
  const idMap = new Map();
  steps.forEach((s, i) => {
    if (s && s.id != null) idMap.set(String(s.id), `step-${base}-${i}-${rand()}`);
  });

  // 2) Two-pass replace sobre el JSON de los steps (placeholder evita colisiones/substrings)
  let json = JSON.stringify(steps);
  let k = 0;
  const placeholders = new Map();
  for (const [oldId, newId] of idMap) {
    const ph = `__WFID_PLACEHOLDER_${k++}__`;
    placeholders.set(ph, newId);
    json = json.split(oldId).join(ph);
  }
  for (const [ph, newId] of placeholders) {
    json = json.split(ph).join(newId);
  }
  const newSteps = JSON.parse(json);

  // 3) Limpiar refs de cuenta por step
  for (const s of newSteps) {
    if (s && s.config) clearAccountRefs(s.config);
  }

  // 4) Limpiar listId en las condiciones del trigger (si las hay)
  const workflowTrigger = src.workflowTrigger || src.trigger || { type: 'contact_created', conditions: {} };
  if (workflowTrigger?.conditions && typeof workflowTrigger.conditions === 'object' && 'listId' in workflowTrigger.conditions) {
    workflowTrigger.conditions = { ...workflowTrigger.conditions, listId: '' };
  }

  return {
    name: (src.name ? `${src.name} (importado)` : 'Flujo importado'),
    description: src.description || '',
    status: 'draft',
    workflowTrigger,
    steps: newSteps,
  };
}
